/**
 * Who's here — the roster screen's read and its five writes (issues #299, #303).
 *
 * **The read came first for somebody else.** `GET /roster` was added for the memory screen:
 * `remember` requires a `subject` on every propose and will not take a default (#92 — *"the
 * family's"* is a claim, not a cautious fallback), so a form that saves a fact has to let
 * somebody say whose it is, and naming a member needs their `userId`. #303 is what the read
 * was actually for.
 *
 * **The five writes are #298's routes onto the tools themselves**, and there is no
 * panel-shaped write here for the same reason there is none in `memory.ts`: removing a member
 * from this screen *is* `remove_member` — the same plain function, the same tier, the same
 * `PROPOSAL#`, the same permanent `ACTION#` (#109) — which is the whole of ADR 0021
 * decision 5. Every one of them proposes and then confirms with `{ confirm, proposalId }` and
 * no body, so a paraphrase cannot change who a write is about. None of them carries a
 * `provenance` field, which is ADR 0014 decision 4's structural floor: no phrasing and no
 * `ai_write_mode` removes somebody in one turn.
 *
 * **Every response type here is derived from the server's route** — ADR 0025 decision 3. This
 * file used to claim they are a *subset* of what the server discloses, on ADR 0021 decision 6
 * and a rule in `memory.ts` that no longer exists; a subset is a second, unchecked statement of
 * the contract, which is right up until the day it is not.
 *
 * **The writes took two rounds to get there, and `DisbandEnvelope` is why that mattered.** An
 * earlier cut of this change left them as the tools' shared {@link WriteEnvelope}, which read as
 * a reasonable stopping point until the re-review named what stood behind it: `DisbandEnvelope`
 * was a hand-written `interface` declaring `doesNotReach` and `erased`, so renaming a field in
 * `disbandFamily.ts` compiled — on the screen that tells a family what deleting their household
 * does *not* reach. That is this PR's own defect, in the one file its sweep had missed.
 *
 * {@link RosterWriteEnvelope} survives as a **bound**, not as a response type: `post` constrains
 * on it, which is what the shared envelope is genuinely for. {@link InviteEnvelope} is the one
 * stated exception, and it is an `Omit` rather than an opt-out — derived, and deliberately
 * withholding one field. See its note on `inviteLink`.
 */

import type { FamilyPayloads } from "@pilotfish/api/src/app/contract.ts";
import {
  type AppApi,
  appFetch,
  confirmWrite,
  familyPath,
  newIdempotencyKey,
  postWrite,
  type StepUpProof,
  type WriteEnvelope,
} from "@pilotfish/app-api";

/**
 * Re-exported so the roster screen's imports stay one module deep (issue #310 moved the
 * bodies out). See `writes.ts`.
 */
export { newIdempotencyKey };

/**
 * **The roster, as the server actually answers it** — `FamilyPayloads["GET /roster"]`, derived
 * from `reads.ts` rather than described here (ADR 0025 decisions 3 and 4).
 *
 * These were four hand-written interfaces until ADR 0025 decision 3, and the swap found what a
 * hand-written copy always eventually holds: `name` was declared `string` while the server's is
 * `string | undefined`, because it comes from a `Map.get` the service's own comment argues can
 * never miss. The panel was asserting a guarantee the server does not make, and would have
 * rendered the string "undefined" into a roster row the day it did.
 *
 * **The optionality that has gone was tolerance for a server that might not send a field**, and
 * it is not load-bearing: the panel ships with its server, so there is no version skew for it
 * to absorb, and optionality would not have helped at runtime anyway — a field the server stops
 * sending is `undefined` whether or not a type admits it. What protects a client that *can* be
 * older than its server is the compatibility rule in ADR 0025 decision 11, plus runtime
 * validation at the mobile boundary. Not a question mark in a browser bundle.
 */
export type Roster = FamilyPayloads["GET /roster"];

/**
 * One member as this screen reads them.
 *
 * `name` is `string | undefined` because the server's is — see {@link Roster}. `isYou` is the
 * server's answer to which row is the caller and is never a comparison made here (#303): the
 * only string this bundle holds about the signed-in person is their address, and comparing it
 * against `email` is wrong the day a member's provider address diverges from their `MEMBER#`
 * row, in the expensive direction.
 */
export type RosterMember = Roster["members"][number];

/**
 * One invitation sent and not yet taken up.
 *
 * `expired` is computed by the server so this screen cannot get the clock wrong. A lapsed
 * invitation is still shown and still cancellable, because whoever holds the link has no way
 * to know it stopped working.
 */
export type PendingInvitation = Roster["invitations"][number];

export function fetchRoster(api: AppApi, familyId: string): Promise<Roster> {
  return appFetch<Roster>(api, familyPath(familyId, "/roster"));
}

/**
 * **The bound every roster write satisfies** — the tools' shared envelope (ADR 0006 decision 5).
 *
 * It used to be this file's *response* type for five routes, which is what the re-review caught:
 * a hand-written shape standing in for what the server sends. Each route now indexes into the
 * contract, and this stays for the job the shared envelope is actually for — constraining
 * {@link post}, so a tail cannot be paired with a body that is not a write envelope at all.
 *
 * `status` is still the field to branch on, never the HTTP code: #298 answers `200` for both a
 * proposal and an apply, so the envelope's own status is the structural statement.
 */
export type RosterWriteEnvelope = WriteEnvelope;

/**
 * `invite_member`'s envelope, **which carries no link on this channel** (#303).
 *
 * The tool returns the one-time onboarding link on the confirmed apply so the inviter can
 * relay it as a fallback when mail does not arrive. That is right for a conversation, where
 * the person asking is the person who would relay it. It is wrong for this screen, which shows
 * no link and offers no way to pass one on — so a live single-use credential would sit in a
 * browser's memory, its devtools and any error report taken from that tab, for nothing.
 *
 * **The suppression is the server's, and it has to be** (PR #330 review). Not declaring the
 * field here hides it from TypeScript and from nothing else: `writeHandler` serialises the
 * tool's envelope unchanged and `appFetch` parses the whole body, so the token would still be
 * in the response bytes. `packages/engine/src/tools/inviteMember.ts` withholds both the field
 * and the URL in `message` unless `auth.canRelayOneTimeSecret` — the capability, never the
 * `arrivedVia` label, because `web` is not provable — and
 * `services/api/tests/app/writes.test.ts` asserts it on the bytes rather than on the DOM.
 *
 * **So this type is derived and then subtracts that one field** — an `Omit`, which is the shape
 * the exception needed rather than an opt-out from the derivation.
 *
 * The general rule — a type that withholds a field the server sends is a second statement of
 * the contract — is why every other envelope here indexes into the contract whole. It bends for
 * exactly one field, and only in the direction of subtracting: a rename anywhere else in this
 * payload still fails to compile, which is what an alias to the shared envelope gave up.
 *
 * The subtraction earns its keep even though the paragraph above says it is not the control. It
 * costs nothing and keeps a live single-use credential out of the type the next person writing
 * this screen reads. Elsewhere the old rule traded a small lie for drift; here it trades nothing
 * for not putting a token in front of somebody.
 */
export type InviteEnvelope = Omit<FamilyPayloads["POST /invite-member"], "inviteLink">;

/**
 * **`Omit` is permissive on both sides, so the subtraction needs two guards, not one**
 * (PR #374, rounds three and four).
 *
 * `Omit<T, K>` takes `K extends keyof any`, so it removes nothing if the key is not on `T` —
 * and that fails in *two* directions, which the first attempt at this guard missed by
 * reasoning about one and checking the other:
 *
 * - **The server renames `inviteLink`.** The `Omit` matches nothing and the renamed field
 *   survives it. Caught by {@link _InviteLinkExists}, an indexed access that is an error on an
 *   absent key.
 * - **The literal on the `Omit` is mistyped or edited.** The `Omit` matches nothing again — and
 *   the first guard still passes, because the server's field is exactly where it always was.
 *   Caught by {@link _InviteLinkIsWithheld}, which asserts the thing that actually matters:
 *   the key is *absent from the result*.
 *
 * The first guard was named for the second's job, which is the part worth remembering. It
 * asserted that the field **exists on the server's payload** while its name claimed the field
 * is **withheld from this type** — true together today, and independent the moment either side
 * moves.
 *
 * `Withheld<T extends true>` is the assertion form because it can fail without a value:
 * `erasableSyntaxOnly` rules out a `const _: True = true`, and this needs none. Both stay
 * beside the type rather than in a test, so a guard and the thing it guards cannot move apart.
 */
type _InviteLinkExists = FamilyPayloads["POST /invite-member"]["inviteLink"];

type Withheld<T extends true> = T;
type _InviteLinkIsWithheld = Withheld<"inviteLink" extends keyof InviteEnvelope ? false : true>;

/**
 * **What a disband answers with** — `FamilyPayloads["POST /disband-family"]`, derived.
 *
 * This was a hand-written `interface` declaring `doesNotReach` and `erased` — the one place
 * this PR's own defect survived its own sweep (PR #374 re-review). Rename `doesNotReach` in
 * `disbandFamily.ts` and it compiled, on the screen that tells a family what deleting their
 * household does *not* reach.
 *
 * The two fields it declared keep their reasons, which are the server's rather than this
 * file's: `doesNotReach` is sent on both replies, and `erased` is reported honestly because
 * the commit and the erasure are separate steps (#228) — the confirm writes a marker and the
 * sweep runs after it, so `status: "applied"` alone would tell a family their data was gone
 * while some of it is still there. `erased` is `null` while proposing.
 */
export type DisbandEnvelope = FamilyPayloads["POST /disband-family"];

/** What a revoke answers with. */
export type RevokeEnvelope = FamilyPayloads["POST /revoke-invitation"];

/** What removing a member answers with. */
export type RemoveEnvelope = FamilyPayloads["POST /remove-member"];

/** What handing ownership over answers with. */
export type TransferEnvelope = FamilyPayloads["POST /transfer-ownership"];

/** What leaving answers with. */
export type LeaveEnvelope = FamilyPayloads["POST /leave-family"];

function post<T extends RosterWriteEnvelope>(
  api: AppApi,
  familyId: string,
  tail: RosterWriteTail,
  body: Record<string, unknown>,
): Promise<T> {
  return postWrite<T>(api, familyId, tail, body);
}

/**
 * The five routes this screen uses, named once — a **narrowing** of `writes.ts`'s
 * {@link WriteTail} rather than a second list of routes.
 *
 * A union rather than a bare string, so {@link confirmRosterWrite} cannot be handed a tail
 * that names no tool — which matters more here than on the memory screen, because
 * `confirmProposal`'s `expectedTool` (#107) refuses a proposal confirmed through the wrong
 * one, and getting it wrong would turn *"yes, remove them"* into a refusal a family cannot
 * act on.
 */
export type RosterWriteTail =
  | "/invite-member"
  | "/revoke-invitation"
  | "/remove-member"
  | "/leave-family"
  | "/transfer-ownership"
  | "/disband-family";

/**
 * Invite somebody in.
 *
 * **`role` is always `adult` and there is no picker**, which is the canvas's own shape: the
 * invite dialog collects an address and nothing else. `owner` is reachable — it is *Make
 * owner* on the member's row once they are here — and offering it at invitation time would
 * mean handing co-ownership to an address before anybody has confirmed it is theirs.
 */
export function proposeInvite(
  api: AppApi,
  familyId: string,
  email: string,
): Promise<InviteEnvelope> {
  return post<InviteEnvelope>(api, familyId, "/invite-member", {
    email,
    role: "adult",
    idempotencyKey: newIdempotencyKey(),
  });
}

/**
 * Call an invitation back.
 *
 * **Addressed by email — the one roster write that is not addressed by an id.** The `INVITE#`
 * sort key is the SHA-256 of the emailed token and is not ours to hand a browser (#115), and
 * for the case this mostly exists for — a mistyped address — the typo is the only string the
 * family has. It withdraws *every* still-pending invitation to that address.
 */
export function proposeRevoke(
  api: AppApi,
  familyId: string,
  email: string,
): Promise<RevokeEnvelope> {
  return post(api, familyId, "/revoke-invitation", {
    email,
    idempotencyKey: newIdempotencyKey(),
  });
}

export function proposeRemove(
  api: AppApi,
  familyId: string,
  memberId: string,
): Promise<RemoveEnvelope> {
  return post(api, familyId, "/remove-member", {
    memberId,
    idempotencyKey: newIdempotencyKey(),
  });
}

export function proposeTransfer(
  api: AppApi,
  familyId: string,
  toMemberId: string,
): Promise<TransferEnvelope> {
  return post(api, familyId, "/transfer-ownership", {
    toMemberId,
    idempotencyKey: newIdempotencyKey(),
  });
}

/**
 * Leave.
 *
 * **No target field at all**, and the absence is the authorization boundary rather than a
 * convenience (#115): there is exactly one member this could remove and the session already
 * fixes them. A `memberId` here would be a way to take somebody else out through a tool that
 * needs no capability.
 */
export function proposeLeave(api: AppApi, familyId: string): Promise<LeaveEnvelope> {
  return post(api, familyId, "/leave-family", { idempotencyKey: newIdempotencyKey() });
}

export function proposeDisband(api: AppApi, familyId: string): Promise<DisbandEnvelope> {
  return post<DisbandEnvelope>(api, familyId, "/disband-family", {
    idempotencyKey: newIdempotencyKey(),
  });
}

/**
 * Apply a stored proposal: `{ confirm: true, proposalId }` and **nothing else**.
 *
 * The body-free confirm is ADR 0011's whole mechanism, so this function takes no payload to
 * send. It is what answers #303's worry that *"remove my husband" is a terrible thing to get
 * wrong from a paraphrase*: on this surface there is no paraphrase — a member is chosen from a
 * list — and the confirm still carries nothing that could change who the write is about.
 */
export function confirmRosterWrite<T extends RosterWriteEnvelope>(
  api: AppApi,
  familyId: string,
  tail: RosterWriteTail,
  proposalId: string,
  /**
   * The second factor, when the server has asked for one (#307).
   *
   * The one thing a confirm may carry besides the id, and it is not an exception to the rule
   * above but the other side of it: it says who is at the keyboard and cannot name a different
   * proposal, tool or family. See `@pilotfish/app-api`'s `StepUpProof`.
   */
  stepUp?: StepUpProof | undefined,
): Promise<T> {
  return confirmWrite<T>(api, familyId, tail, proposalId, stepUp);
}
